import { AppHTTPService } from '@/api/http-service'
import { FieldsContext } from '@/contexts/FieldsContext'
import { useRulesContext } from '@/contexts/RulesContext'
import { useSheet } from '@/contexts/SheetContext'
import { FieldConfig } from '@flatfile/api'
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FilterByValueOption,
  Icon,
  RichMentionsContext,
} from '@flatfile/baseline'
import {
  ColumnContextMenuSlotProps,
  useColumnSelectionContext,
} from '@flatfile/turntable'
import { useQueryClient } from '@tanstack/react-query'
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { MentionsWrapper } from '../Mentions/MentionsWrapper'
import { AddFieldPanel } from './AddFieldPanel'
import { EditFieldPanel } from './EditFieldPanel'
import useColumnContextMenu from './hooks/useColumnContextMenu'
import { MainMenuPanel } from './MainMenuPanel'
import { getDisplayType } from './types'

type SheetColumnContextMenuProps = ColumnContextMenuSlotProps & {
  className?: string
  handleFindAndReplace: (props: any) => void
  getJobQuery: () => any
  onQueryByValue: (field: string, clearExisting?: boolean) => void
  onQueryBySearchValue: (field: string) => void
  isLastColumn: boolean
  openInput: { columnId: string; inputType: string } | null
  setOpenInput: React.Dispatch<
    React.SetStateAction<{ columnId: string; inputType: string } | null>
  >
  setShowEnrichment: (showEnrichment: boolean) => void
  showEnrichment: boolean
}

// Enum for menu state management
enum MenuState {
  MAIN = 'main',
  EDIT_FIELD = 'edit_field',
  EDIT_FIELD_TYPE = 'edit_field_type',
  ADD_FIELD = 'add_field',
  ADD_FIELD_TYPE = 'add_field_type',
}

const SheetColumnContextMenu = ({
  column,
  isColumnDisabled,
  isPinDisabled,
  isPinned,
  isSortDisabled,
  onMenuOpenChange,
  openFindAndReplaceModal,
  openSearchValueModal,
  onQueryByValue,
  onQueryBySearchValue,
  togglePinnedColumn,
  getJobQuery,
  isLastColumn,
  openInput,
  setOpenInput,
  setShowEnrichment,
  showEnrichment,
}: SheetColumnContextMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [isChangingType, setIsChangingType] = useState(false)
  const [typeChangeError, setTypeChangeError] = useState<string | null>(null)
  const mentionsContext = useContext(RichMentionsContext)
  const [newDestinationColumnName, setNewDestinationColumnName] = useState('')
  const [typeSearchValue, setTypeSearchValue] = useState('')
  const { sheet } = useSheet()
  const enrichments = (sheet?.enrichments || []).filter(
    (enrichment: any) => enrichment.config.targetColumn === column.value
  )

  const { columnConfig } = useContext(FieldsContext)
  const { setColumnSelections } = useColumnSelectionContext()
  const queryClient = useQueryClient()
  const columnType = column.type
  const selectedType = getDisplayType(columnType)
  const { addPendingRules } = useRulesContext()

  // State for field updates
  const [isSavingField, setIsSavingField] = useState(false)
  const [isAddingField, setIsAddingField] = useState(false)

  // Enum-based state management
  const [menuState, setMenuState] = useState<MenuState>(MenuState.MAIN)
  const [addFieldPosition, setAddFieldPosition] = useState<'left' | 'right'>(
    'right'
  )

  // Derived boolean properties for transition compatibility
  const showEditField = useMemo(
    () =>
      menuState === MenuState.EDIT_FIELD ||
      menuState === MenuState.EDIT_FIELD_TYPE,
    [menuState]
  )
  const showFieldType = useMemo(
    () => menuState === MenuState.EDIT_FIELD_TYPE,
    [menuState]
  )
  const showAddField = useMemo(
    () =>
      menuState === MenuState.ADD_FIELD ||
      menuState === MenuState.ADD_FIELD_TYPE,
    [menuState]
  )
  const showAddFieldType = useMemo(
    () => menuState === MenuState.ADD_FIELD_TYPE,
    [menuState]
  )

  // State setters that maintain enum logic
  const setShowEditField = (show: boolean) => {
    if (show) {
      setMenuState(MenuState.EDIT_FIELD)
    } else {
      setMenuState(MenuState.MAIN)
    }
  }

  const setShowFieldType = (show: boolean) => {
    if (show) {
      setMenuState(MenuState.EDIT_FIELD_TYPE)
    } else {
      setMenuState(MenuState.EDIT_FIELD)
    }
  }

  const setShowAddField = (show: boolean) => {
    if (show) {
      setMenuState(MenuState.ADD_FIELD_TYPE)
    } else {
      setMenuState(MenuState.MAIN)
    }
  }

  const setShowAddFieldType = (show: boolean) => {
    if (show) {
      setMenuState(MenuState.ADD_FIELD_TYPE)
    } else {
      setMenuState(MenuState.ADD_FIELD)
    }
  }

  const hasSetPositionFixed = useRef(false)
  useEffect(() => {
    if (
      mentionsContext &&
      typeof mentionsContext.setPositionFixed === 'function' &&
      !hasSetPositionFixed.current
    ) {
      mentionsContext.setPositionFixed(false)
      hasSetPositionFixed.current = true
    }
  }, [mentionsContext])

  const {
    tSortAscending,
    tSortDescending,
    tPinColumn,
    filterOptions,
    onPinSelected,
    onSortAscendingSelected,
    onSortDescendingSelected,
    onSearchByValueSelected,
    onReplaceEmptyCellsSelected,
    onFilterByValueOpenChange,
    onFilterByValueClose,
    onRoutineSelected,
    selectedFilterOptions,
    setSelectedFilterOptions,
    fetchingFilterOptions,
    filterSearchValue,
    onFilterSearchValueChange,
    onFindAndReplaceSelected,
  } = useColumnContextMenu({
    column,
    isPinned,
    togglePinnedColumn,
    openSearchValueModal,
    openFindAndReplaceModal,
    getJobQuery,
  })
  useEffect(() => onMenuOpenChange?.(isOpen), [isOpen, onMenuOpenChange])

  // Handler to invoke new QUERY BY VALUE feature in search bar
  const handleQueryByValue = () => {
    onQueryByValue(column.value, true)
  }

  // Handler to invoke new QUERY BY SEARCH VALUE feature in search bar
  const handleQueryBySearchValue = () => {
    onQueryBySearchValue(column.value)
  }

  const handleMouseDown = (e: any) => {
    e.stopPropagation()
  }

  // Close handlers that actually close the menu
  const handleCloseMenu = () => {
    setMenuState(MenuState.MAIN)
    setIsOpen(false)
  }

  // Get current field data from column config
  const getCurrentFieldData = () => {
    const fieldConfig = columnConfig.find((col) => col.value === column.value)

    // Get existing enrichment prompt for this column
    const existingEnrichment = enrichments.find(
      (enrichment: any) => enrichment.config.targetColumn === column.value
    )
    const existingPrompt = existingEnrichment?.config?.prompt || ''
    const existingRunInBackground = existingEnrichment?.runInBackground || false

    return {
      name: fieldConfig?.label || column.value,
      type: columnType,
      prompt: existingPrompt,
      runInBackground: existingRunInBackground,
    }
  }

  // Handle field save
  const handleFieldSave = async (fieldData: {
    name: string
    type: string
    aiAutofill?: boolean
    prompt?: string
  }) => {
    if (!sheet?.id) {
      throw new Error('Sheet not found')
    }

    setIsSavingField(true)
    try {
      // Find the current field in the sheet schema
      const currentFields = sheet.schema?.fields || []
      const fieldIndex = currentFields.findIndex((f) => f.key === column.value)

      if (fieldIndex === -1) {
        throw new Error('Field not found')
      }

      // Create updated field configuration
      const updatedField = {
        ...currentFields[fieldIndex],
        label: fieldData.name,
        type: fieldData.type,
      } as FieldConfig

      // Create updated fields array
      const updatedFields = [...currentFields]
      updatedFields[fieldIndex] = updatedField

      // Update the sheet via API
      const response = await AppHTTPService.client.sheets.update(sheet.id, {
        schema: {
          fields: updatedFields,
        },
      })

      if (!response.data) {
        throw new Error('Failed to update field')
      }

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['sheets', sheet.id] })
    } catch (error) {
      console.error('Error updating field:', error)
      throw error
    } finally {
      setIsSavingField(false)
    }
  }

  const handleTypeChange = async (newType: string) => {
    // Only apply the type change when Save is clicked
    if (menuState === MenuState.EDIT_FIELD_TYPE) {
      setMenuState(MenuState.EDIT_FIELD)
    } else if (menuState === MenuState.ADD_FIELD_TYPE) {
      // We're adding a new field - just update the selected type and go back to add field panel
      // The AddFieldPanel will handle the type selection internally
      setMenuState(MenuState.ADD_FIELD)
    }
  }

  const onRunInngest = async (
    enrichmentType: string,
    prompt: string,
    overrideColumnKey?: string,
    runInBackground: boolean = false
  ) => {
    // Use overrideColumnKey if provided (for smart fill), otherwise use newDestinationColumnName
    const finalDestinationFieldKey =
      overrideColumnKey || newDestinationColumnName
    const sheetId = sheet?.id

    if (!sheetId || !columnConfig) {
      console.error('Missing required data for enrichment')
      return
    }
    const enrichment = {
      name: finalDestinationFieldKey + ' ' + enrichmentType,
      type: enrichmentType,
      key: finalDestinationFieldKey,
      runInBackground,
      config: {
        prompt,
        provider: 'exa',
        targetColumn: finalDestinationFieldKey,
      },
    }

    const { data: currentEnrichments } =
      await AppHTTPService.client.sheets.get(sheetId)
    const filteredEnrichments = currentEnrichments.enrichments?.filter(
      (enrichment: any) => enrichment.key !== finalDestinationFieldKey
    )
    const updatedEnrichments = [...filteredEnrichments, enrichment]
    const result = await AppHTTPService.client.sheets.update(sheetId, {
      enrichments: updatedEnrichments,
    })

    // The sheet update will automatically enable background enrichment listening
    switch (enrichmentType) {
      case 'translate': {
        // Send only the prompt and targetColumn; backend/LLM will infer the rest
        const translationEnrichment = {
          name: 'translate',
          key: finalDestinationFieldKey,
          config: {
            prompt,
            targetColumn: finalDestinationFieldKey,
            runInBackground,
          },
        }

        const result = await AppHTTPService.client.enrichments.translate({
          sheetId,
          enrichment: translationEnrichment,
          columnConfig,
        })

        break
      }

      case 'web-search': {
        const webSearchEnrichment = {
          name: 'research',
          key: finalDestinationFieldKey,
          config: {
            targetColumn: finalDestinationFieldKey,
            sourceColumn: 'City', //column.value,
            provider: 'exa', // Default to Exa provider
            prompt: prompt, // Use the input value as the search query
            runInBackground,
          },
        }

        const result = await AppHTTPService.client.enrichments.researchInngest({
          sheetId,
          enrichment: webSearchEnrichment,
          columnConfig,
        })

        break
      }

      case 'smart-fill':
      default: {
        // Handle prompt-based enrichment (including smart-fill)
        const parsedEnrichmentValue = prompt.replace(
          /@\{\{\{(.+?)\}\}\}/g,
          (match, jsonString) => {
            try {
              const parsedJson = JSON.parse(`{${jsonString}}`)
              if (parsedJson && parsedJson.name) {
                return `{{${parsedJson.name}}}`
              }
            } catch (error) {
              console.error('Error parsing mention JSON:', error)
              return match
            }
            return match
          }
        )

        const promptEnrichment = {
          name: enrichmentType === 'smart-fill' ? 'smart-fill' : 'prompt',
          key: finalDestinationFieldKey,
          config: {
            prompt: parsedEnrichmentValue,
            targetColumn: finalDestinationFieldKey,
            ...(enrichmentType === 'smart-fill' && {
              sourceColumn: column.value,
            }),
            runInBackground,
          },
        }

        const result = await AppHTTPService.client.enrichments.prompt({
          sheetId,
          enrichment: promptEnrichment,
          columnConfig,
        })

        break
      }
    }
  }

  // Handle field creation
  const handleFieldAdd = async (fieldData: {
    name: string
    type: string
    aiAutofill?: boolean
    prompt?: string
    position: 'left' | 'right'
  }) => {
    if (!sheet?.id) {
      throw new Error('Sheet not found')
    }
    const newRule = {
      type: 'add-field',
      name: 'Add Field',
      config: {
        type: 'add-field',
        name: fieldData.name.trim(),
        destinationField: fieldData.name.trim(),
        fieldType: fieldData.type,
        changes: null,
        sourceField: column.value,
        position: fieldData.position,
      },
    }

    await AppHTTPService.client.sheets.update(sheet?.id, {
      addedRules: [newRule],
    })
    const smartFieldTypes = ['translate', 'web-search', 'smart-fill']

    if (smartFieldTypes.includes(fieldData.type)) {
      const runInBackground = true
      onRunInngest(
        fieldData.type,
        fieldData.prompt,
        fieldData.name,
        runInBackground
      )
    }
  }

  const [menuHeight, setMenuHeight] = useState<number | undefined>(200)
  const mainPanelRef = useRef<HTMLDivElement>(null)
  const editPanelRef = useRef<HTMLFormElement>(null)
  const fieldTypePanelRef = useRef<HTMLDivElement>(null)
  const addFieldPanelRef = useRef<HTMLFormElement>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)

  // Helper to update menu height based on active panel
  function updateMenuHeight() {
    let ref: React.RefObject<HTMLElement> | null = null
    if (showFieldType || showAddFieldType) {
      ref = fieldTypePanelRef
    } else if (showEditField) {
      ref = editPanelRef
    } else if (showAddField) {
      ref = addFieldPanelRef
    } else {
      ref = mainPanelRef
    }
    if (ref && ref.current) {
      setMenuHeight(ref.current.offsetHeight + 16)
    }
  }

  // Set up ResizeObserver to monitor panel size changes
  useEffect(() => {
    if (!isOpen) return

    // Clean up existing observer
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect()
    }

    // Create new ResizeObserver
    resizeObserverRef.current = new ResizeObserver(() => {
      updateMenuHeight()
    })

    // Observe all panel refs
    const refs = [
      mainPanelRef,
      editPanelRef,
      fieldTypePanelRef,
      addFieldPanelRef,
    ]
    refs.forEach((ref) => {
      if (ref.current) {
        resizeObserverRef.current?.observe(ref.current)
      }
    })

    // Cleanup function
    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect()
      }
    }
  }, [isOpen, menuState])

  // Update height on panel change
  useLayoutEffect(() => {
    updateMenuHeight()
  }, [menuState])

  // Also update on open
  useLayoutEffect(() => {
    if (isOpen) {
      // Small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        updateMenuHeight()
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  const currentFieldData = getCurrentFieldData()

  return (
    <div className='relative'>
      <DropdownMenu
        open={isOpen}
        modal={false}
        onOpenChange={(openState) => {
          // Only allow outside clicks to close the menu when on the main panel
          if (!openState && menuState !== MenuState.MAIN) {
            // Don't close the menu if we're not on the main panel
            return
          }
          setIsOpen(openState)
          if (!openState) {
            setMenuState(MenuState.MAIN)
          }
        }}
      >
        <DropdownMenuTrigger asChild>
          <button
            className={cn(
              'hover:bg-accent size-5 rounded-sm group-hover:visible flex items-center justify-center outline-none',
              {
                invisible: !isOpen,
                'visible bg-accent': isOpen,
              }
            )}
          >
            <Icon size={12} name='dot-grid-1x3-horizontal' />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align='end'
          className='w-56'
          style={{
            height: menuHeight ? `${menuHeight}px` : 'auto',
            transition: 'height 0.3s cubic-bezier(0.4,0,0.2,1)',
            overflow: 'hidden',
          }}
          onMouseDown={handleMouseDown}
          data-native-context
        >
          <MentionsWrapper>
            <div className='w-full relative overflow-hidden h-full'>
              {/* Main Context Menu */}
              <div
                className={`transition-transform duration-300 ${showEditField || showAddField ? '-translate-x-full' : 'translate-x-0'}`}
              >
                <MainMenuPanel
                  mainPanelRef={mainPanelRef}
                  showEditField={showEditField}
                  setShowEditField={setShowEditField}
                  showAddField={showAddField}
                  setShowAddField={setShowAddField}
                  setAddFieldPosition={setAddFieldPosition}
                  onSortAscendingSelected={onSortAscendingSelected}
                  onSortDescendingSelected={onSortDescendingSelected}
                  onPinSelected={onPinSelected}
                  onHideInView={() => {
                    // TODO: Implement hide in view functionality
                    console.log('Hide in view clicked')
                  }}
                  onDuplicate={() => {
                    // TODO: Implement duplicate functionality
                    console.log('Duplicate clicked')
                  }}
                  onClearColumn={() => {
                    // TODO: Implement clear column functionality
                    console.log('Clear column clicked')
                  }}
                  onDeleteColumn={() => {
                    // TODO: Implement delete column functionality
                    console.log('Delete column clicked')
                  }}
                  onVisualizeData={() => {
                    // TODO: Implement visualize data functionality
                    console.log('Visualize data clicked')
                  }}
                />
              </div>

              {/* Edit Field Panel */}
              <div
                className={`absolute top-0 left-0 w-full h-full bg-[#ffffff] transition-transform duration-300 ${showEditField ? 'translate-x-0' : 'translate-x-full'}`}
              >
                <EditFieldPanel
                  onBack={() => setShowEditField(false)}
                  onClose={handleCloseMenu}
                  onShowFieldType={() => setShowFieldType(true)}
                  showFieldType={showFieldType}
                  onHideFieldType={() => setShowFieldType(false)}
                  fieldTypePanelRef={fieldTypePanelRef}
                  editFieldPanelRef={editPanelRef}
                  onTypeChange={handleTypeChange}
                  currentColumnType={columnType}
                  currentFieldName={currentFieldData.name}
                  currentPrompt={currentFieldData.prompt}
                  currentRunInBackground={currentFieldData.runInBackground}
                  onSave={handleFieldSave}
                  isSaving={isSavingField}
                />
              </div>

              {/* Add Field Panel */}
              <div
                className={`absolute top-0 left-0 w-full h-full bg-[#ffffff] transition-transform duration-300 ${showAddField ? 'translate-x-0' : 'translate-x-full'}`}
              >
                <AddFieldPanel
                  onBack={() => setShowAddField(false)}
                  onClose={handleCloseMenu}
                  onShowFieldType={() => setShowAddFieldType(true)}
                  showAddField={showAddField}
                  showFieldType={showAddFieldType}
                  onHideFieldType={() => setShowAddFieldType(false)}
                  fieldTypePanelRef={fieldTypePanelRef}
                  addFieldPanelRef={addFieldPanelRef}
                  position={addFieldPosition}
                  onTypeChange={handleTypeChange}
                  onAddField={handleFieldAdd}
                  isAddingField={isAddingField}
                />
              </div>
            </div>
          </MentionsWrapper>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

type FilterByValueDropdownProps = {
  onFilterByValueOpenChange: (open: boolean) => void
  onFilterSearchValueChange: (value: string) => void
  onFilterByValueClose: () => void
  fetchingFilterOptions: boolean
  selectedFilterOptions: FilterByValueOption[]
  setSelectedFilterOptions: Dispatch<SetStateAction<FilterByValueOption[]>>
  filterSearchValue: any
  filterOptions: FilterByValueOption[]
}

const FilterByValueItem = ({ onSelect }: { onSelect: () => void }) => {
  return (
    <DropdownMenuItem
      tid='sheet.table.contextMenu.filterByValue.label'
      onSelect={onSelect}
    />
  )
}

export default SheetColumnContextMenu
